import React from 'react';
import { Input, Tag } from 'antd';

interface ObjectNodePropertyPanelProps {
  nodeId: string;
  parentNodeId: string;
  name: string;
  nameError: string | null;
  codeName: string;
  codeNameError: string | null;
  description: string;
  namespace: string;
  packageName: string;
  packageNameError: string | null;
  packageNameDisabled: boolean;
  tags: string[];
  tagInput: string;
  exampleValues: string;
  parentObjects: string[];
  childObjects: string[];
  exhibitionAttributes: string[];
  defaultAttributes: string[];
  inheritedAttributes: string[];
  relationStats: {
    exhibition: number;
    conversion: number;
    agent: number;
    instrument: number;
  };
  relatedSystem: string;
  nodePositionText: string;
  nameInputRef: React.RefObject<any>;
  codeNameInputRef: React.RefObject<any>;
  descriptionInputRef: React.RefObject<any>;
  packageNameInputRef: React.RefObject<any>;
  onNameChange: (value: string) => void;
  onNameFocus: () => void;
  onNameBlur: (value: string) => void;
  onCodeNameChange: (value: string) => void;
  onCodeNameFocus: () => void;
  onCodeNameBlur: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onDescriptionFocus: () => void;
  onDescriptionBlur: (value: string) => void;
  onPackageNameChange: (value: string) => void;
  onPackageNameFocus: () => void;
  onPackageNameBlur: (value: string) => void;
  onTagInputChange: (value: string) => void;
  onAddTag: () => void;
  onRemoveTag: (tag: string) => void;
  onExampleValuesChange: (value: string) => void;
  onExampleValuesFocus: () => void;
  onExampleValuesBlur: (value: string) => void;
  renderChipList: (
    items: string[],
    emptyText?: string,
    colorClass?: string
  ) => React.ReactNode;
}

const FieldBlock: React.FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div>{children}</div>
    </div>
  );
};

const ReadonlyTextValue: React.FC<{
  value: string;
}> = ({ value }) => {
  return <div className="text-sm text-gray-700 break-all">{value || '-'}</div>;
};

const DEFAULT_PACKAGE_NAME_PLACEHOLDER = '请输入包名，如 com.example';
const DISABLED_PACKAGE_NAME_PLACEHOLDER = '继承上级包名，不可单独设置';
const DISABLED_PACKAGE_NAME_TITLE = '当前节点继承上级包名，不可单独设置';

const SectionBlock: React.FC<{
  title: string;
  children: React.ReactNode;
}> = ({ title, children }) => {
  return (
    <section className="flex flex-col gap-4">
      <div className="text-sm font-semibold text-gray-800">{title}</div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
};

const ObjectNodePropertyPanel: React.FC<ObjectNodePropertyPanelProps> = ({
  nodeId,
  parentNodeId,
  name,
  nameError,
  codeName,
  codeNameError,
  description,
  namespace,
  packageName,
  packageNameError,
  packageNameDisabled,
  tags,
  tagInput,
  exampleValues,
  parentObjects,
  childObjects,
  exhibitionAttributes,
  defaultAttributes,
  inheritedAttributes,
  relationStats,
  relatedSystem,
  nodePositionText,
  nameInputRef,
  codeNameInputRef,
  descriptionInputRef,
  packageNameInputRef,
  onNameChange,
  onNameFocus,
  onNameBlur,
  onCodeNameChange,
  onCodeNameFocus,
  onCodeNameBlur,
  onDescriptionChange,
  onDescriptionFocus,
  onDescriptionBlur,
  onPackageNameChange,
  onPackageNameFocus,
  onPackageNameBlur,
  onTagInputChange,
  onAddTag,
  onRemoveTag,
  onExampleValuesChange,
  onExampleValuesFocus,
  onExampleValuesBlur,
  renderChipList,
}) => {
  return (
    <div className="flex flex-col gap-6">
      <SectionBlock title="基本信息">
        <div className="flex flex-col gap-2">
          <FieldBlock label="命名空间">
            <ReadonlyTextValue value={namespace} />
          </FieldBlock>

          <FieldBlock label="包名">
            <div className="flex flex-col gap-1">
              <Input
                ref={packageNameInputRef}
                value={packageName}
                placeholder={
                  packageNameDisabled
                    ? DISABLED_PACKAGE_NAME_PLACEHOLDER
                    : DEFAULT_PACKAGE_NAME_PLACEHOLDER
                }
                title={
                  packageNameDisabled ? DISABLED_PACKAGE_NAME_TITLE : undefined
                }
                status={packageNameError ? 'error' : undefined}
                disabled={packageNameDisabled}
                onChange={e => onPackageNameChange(e.target.value)}
                onFocus={onPackageNameFocus}
                onBlur={e => onPackageNameBlur(e.target.value)}
                onPressEnter={e => (e.target as HTMLInputElement).blur()}
              />
              {packageNameError && (
                <div className="text-xs text-red-500">{packageNameError}</div>
              )}
            </div>
          </FieldBlock>

          <FieldBlock label="节点ID">
            <ReadonlyTextValue value={nodeId} />
          </FieldBlock>
        </div>

        <FieldBlock label="对象名称">
          <div className="flex flex-col gap-1">
            <Input
              ref={nameInputRef}
              value={name}
              placeholder="请输入名称"
              maxLength={20}
              showCount
              status={nameError ? 'error' : undefined}
              onChange={e => onNameChange(e.target.value)}
              onFocus={onNameFocus}
              onBlur={e => onNameBlur(e.target.value)}
              onPressEnter={e => (e.target as HTMLInputElement).blur()}
            />
            {nameError && (
              <div className="text-xs text-red-500">{nameError}</div>
            )}
          </div>
        </FieldBlock>

        <FieldBlock label="英文名称">
          <div className="flex flex-col gap-1">
            <Input
              ref={codeNameInputRef}
              value={codeName}
              placeholder="请输入英文名称"
              maxLength={200}
              showCount
              status={codeNameError ? 'error' : undefined}
              onChange={e => onCodeNameChange(e.target.value)}
              onFocus={onCodeNameFocus}
              onBlur={e => onCodeNameBlur(e.target.value)}
              onPressEnter={e => (e.target as HTMLInputElement).blur()}
            />
            {codeNameError && (
              <div className="text-xs text-red-500">{codeNameError}</div>
            )}
          </div>
        </FieldBlock>

        <FieldBlock label="描述">
          <Input.TextArea
            ref={descriptionInputRef}
            value={description}
            placeholder="请输入描述（可选）"
            rows={4}
            maxLength={200}
            showCount
            onChange={e => onDescriptionChange(e.target.value)}
            onFocus={onDescriptionFocus}
            onBlur={e => onDescriptionBlur(e.target.value)}
          />
        </FieldBlock>

        <FieldBlock label="标签">
          <div className="flex flex-wrap items-center gap-2">
            {tags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={() => onRemoveTag(tag)}
                className="!m-0"
              >
                {tag}
              </Tag>
            ))}
            <Input
              size="small"
              value={tagInput}
              placeholder="+NewTag"
              onChange={e => onTagInputChange(e.target.value)}
              maxLength={5}
              onPressEnter={onAddTag}
              onBlur={onAddTag}
              className="!w-24"
            />
          </div>
        </FieldBlock>

        <FieldBlock label="示例值">
          <Input
            value={exampleValues}
            placeholder="多个示例值用英文逗号隔开"
            onChange={e => onExampleValuesChange(e.target.value)}
            onFocus={onExampleValuesFocus}
            onBlur={e => onExampleValuesBlur(e.target.value)}
            onPressEnter={e => (e.target as HTMLInputElement).blur()}
          />
        </FieldBlock>
      </SectionBlock>

      <SectionBlock title="结构信息">
        <FieldBlock label="父对象">{renderChipList(parentObjects)}</FieldBlock>
        <FieldBlock label="子对象">{renderChipList(childObjects)}</FieldBlock>
      </SectionBlock>

      <SectionBlock title="属性概览">
        <FieldBlock label="展示关系的属性">
          {renderChipList(
            exhibitionAttributes,
            '-',
            'text-green-600 border-green-200'
          )}
        </FieldBlock>
        <FieldBlock label="默认属性">
          {renderChipList(
            defaultAttributes,
            '-',
            'text-blue-500 border-blue-200'
          )}
        </FieldBlock>
        <FieldBlock label="父类对象继承的属性">
          {renderChipList(
            inheritedAttributes,
            '-',
            'text-orange-500 border-orange-200'
          )}
        </FieldBlock>
      </SectionBlock>

      <SectionBlock title="关联关系">
        <FieldBlock label="结构关系">
          <div className="text-xs text-gray-600">
            展示关系数量：{relationStats.exhibition}
          </div>
        </FieldBlock>
        <FieldBlock label="过程关系">
          <div className="flex flex-col gap-1 text-xs text-gray-600">
            <div>转换关系数量：{relationStats.conversion}</div>
            <div>agent关系数量：{relationStats.agent}</div>
            <div>工具关系数量：{relationStats.instrument}</div>
          </div>
        </FieldBlock>
      </SectionBlock>

      <SectionBlock title="其他信息">
        <FieldBlock label="所属父节点ID">
          <ReadonlyTextValue value={parentNodeId} />
        </FieldBlock>
        <FieldBlock label="关联系统">
          <span className="text-sm text-gray-700">{relatedSystem || '-'}</span>
        </FieldBlock>
        <FieldBlock label="坐标">
          <span className="text-sm text-gray-700">{nodePositionText}</span>
        </FieldBlock>
      </SectionBlock>
    </div>
  );
};

export default ObjectNodePropertyPanel;
